import React, { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams, Navigate } from 'react-router-dom'
import { setToken } from '@/lib/api'
import { Input } from '@/components/atoms/Input'
import { Select } from '@/components/atoms/Select'
import { MortiseMark } from '@/components/atoms/MortiseMark'
import { isIOS, isNativePlatform } from '@/lib/native'
import MobileSignupPage from '@/pages/MobileSignupPage'

const SIZE_BANDS = ['1', '2-5', '6-15', '16-50', '50+'] as const
const TRADE_OPTIONS = [
  { value: '', label: 'What do you build?' },
  { value: 'General contractor', label: 'General contractor' },
  { value: 'Remodeller', label: 'Remodeller' },
  { value: 'Home builder', label: 'Home builder' },
  { value: 'Subcontractor', label: 'Subcontractor' },
  { value: 'Other', label: 'Other' },
]
const ACCOUNTING_OPTIONS = [
  { value: '', label: 'Current accounting software' },
  { value: 'QuickBooks Online', label: 'QuickBooks Online' },
  { value: 'Sage', label: 'Sage' },
  { value: 'Xero', label: 'Xero' },
  { value: 'Other', label: 'Other' },
  { value: 'None', label: 'None' },
]

export default function SignupPage() {
  // App Store Guideline 4 + 3.1.1 + Joe's "no mobile signup" directive: the
  // native iOS app must NOT offer account creation at all (no password form
  // after Sign in with Apple; no signup-to-a-paid-service surface). Any attempt
  // to reach /signup inside the native shell — direct nav, stale deep-link, or
  // a Sign-in-with-Apple no-account redirect — bounces to /login, where the
  // form-less "existing customers only" notice renders. Accounts are created on
  // the web (usetenon.com) only. Mobile Safari on iOS is still the web and keeps
  // the real form; only the Capacitor native shell is gated.
  if (isNativePlatform()) {
    return <Navigate to="/login?sso_no_account=1" replace />
  }
  // App Store 3.1.1 — mobile-native form collects name/company/email/password
  // with zero billing copy. Android and web get the full form.
  if (isIOS()) {
    return <MobileSignupPage />
  }
  return <SignupFormPage />
}

function SignupFormPage() {
  useEffect(() => {
    document.title = "Tenon — Sign Up"
    return () => { document.title = "Tenon" }
  }, [])

  const [searchParams] = useSearchParams()
  const oauthMethod = searchParams.get('method') // 'google' | 'apple' | null

  const [companyName, setCompanyName]         = useState('')
  const [name, setName]                       = useState(() => searchParams.get('name') || '')
  const [email, setEmail]                     = useState(() => searchParams.get('email') || '')
  const [password, setPassword]               = useState('')
  const [confirm, setConfirm]                 = useState('')
  const [companySizeBand, setCompanySizeBand] = useState('')
  const [primaryTrade, setPrimaryTrade]       = useState('')
  const [accountingSoftware, setAccountingSoftware] = useState('')
  const [termsAccepted, setTermsAccepted]     = useState(false)
  const [error, setError]                     = useState<string | null>(null)
  const [loading, setLoading]                 = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password !== confirm) { setError('Passwords do not match'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (!termsAccepted) { setError('You must agree to the Terms of Service and Privacy Policy'); return }
    setLoading(true)
    try {
      const res = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          name,
          email,
          password,
          company_size_band: companySizeBand,
          primary_trade: primaryTrade,
          accounting_software: accountingSoftware,
          utm_source: searchParams.get('utm_source') ?? '',
          utm_medium: searchParams.get('utm_medium') ?? '',
          utm_campaign: searchParams.get('utm_campaign') ?? '',
          signup_source: searchParams.get('source') ?? '',
          terms_accepted: termsAccepted,
        }),
      })
      const data = await res.json()
      if (!res.ok) { throw new Error(data.error || 'Signup failed') }
      await setToken(data.token)
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]))
        if (payload.exp) localStorage.setItem('ml_token_exp', String(payload.exp))
      } catch (_) { /* ignore */ }
      navigate('/onboarding', { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Signup failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      fontFamily: "'Karla', -apple-system, sans-serif",
      padding: '24px 16px',
      position: 'relative',
    }}>
      {/* Gold gradient ambient */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(80% 60% at 100% 0%, color-mix(in oklch, var(--brand-gold) 10%, transparent), transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>

        <a href="https://usetenon.com" style={{ display: 'flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <MortiseMark size={44} light />
        </a>

        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '36px 40px',
          boxShadow: 'var(--shadow-2)',
        }}>
          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-gold)', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-muted)', fontFamily: "'Karla', -apple-system, sans-serif" }}>
              Tenon · Create Account
            </span>
          </div>

          <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
            Create your account
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: oauthMethod ? 16 : 28, marginTop: 0, lineHeight: 1.55 }}>
            30-day free trial · Card required at setup.
          </p>

          {oauthMethod && (
            <div style={{
              padding: '10px 14px',
              background: 'color-mix(in oklch, var(--brand-gold) 8%, var(--bg-elevated))',
              border: '1px solid color-mix(in oklch, var(--brand-gold) 25%, var(--border))',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginBottom: 12,
            }}>
              {oauthMethod === 'google' ? '🔗 Signing up via Google' : '🔗 Signing up via Apple'} — your email is pre-filled. Set a password to finish.
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && (
              <div style={{
                padding: '10px 14px',
                background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                borderRadius: 8,
                color: 'var(--st-error)',
                fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Company name</label>
              <Input type="text" placeholder="Acme Construction" value={companyName} onChange={e => setCompanyName(e.target.value)} required autoFocus />
            </div>

            {/* ICP: company size band */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Team size</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {SIZE_BANDS.map(band => (
                  <button
                    key={band}
                    type="button"
                    onClick={() => setCompanySizeBand(band)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: 7,
                      border: `1px solid ${companySizeBand === band ? 'var(--brand-gold)' : 'var(--border)'}`,
                      background: companySizeBand === band
                        ? 'color-mix(in oklch, var(--brand-gold) 12%, var(--bg-elevated))'
                        : 'var(--bg-elevated)',
                      color: companySizeBand === band ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontFamily: "'Karla', -apple-system, sans-serif",
                      fontSize: 13,
                      fontWeight: companySizeBand === band ? 700 : 500,
                      cursor: 'pointer',
                      transition: 'border-color 100ms, background 100ms',
                    }}
                  >
                    {band}
                  </button>
                ))}
              </div>
            </div>

            {/* ICP: primary trade */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>What do you build?</label>
              <Select
                options={TRADE_OPTIONS.slice(1)}
                placeholder="What do you build?"
                value={primaryTrade}
                onChange={e => setPrimaryTrade(e.target.value)}
              />
            </div>

            {/* ICP: accounting software */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Accounting software</label>
              <Select
                options={ACCOUNTING_OPTIONS.slice(1)}
                placeholder="Current accounting software"
                value={accountingSoftware}
                onChange={e => setAccountingSoftware(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Your name</label>
              <Input type="text" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} required />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Work email</label>
              <Input type="email" placeholder="jane@acmeconstruction.com" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Password</label>
              <Input type="password" placeholder="Min. 8 characters" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Confirm password</label>
              <Input type="password" placeholder="Repeat password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                required
                style={{ marginTop: 2, accentColor: 'var(--brand-gold)', flexShrink: 0 }}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Privacy Policy</Link>.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px',
                background: loading ? 'color-mix(in oklch, var(--brand-gold) 60%, transparent)' : 'var(--brand-gold)',
                color: 'var(--text-primary)',
                fontFamily: "'Karla', -apple-system, sans-serif",
                fontSize: 15, fontWeight: 700, border: 'none', borderRadius: 8,
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: '-0.01em', marginTop: 4,
              }}
            >
              {loading ? 'Creating account…' : 'Start free trial'}
            </button>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--text-primary)', fontWeight: 700, textDecoration: 'none', borderBottom: '2px solid var(--brand-gold)', paddingBottom: 1 }}>
                Sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Sage callout */}
        <div style={{
          marginTop: 24,
          background: 'color-mix(in oklch, var(--brand-gold) 7%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--brand-gold) 18%, var(--border-subtle))',
          borderRadius: 10,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            background: 'color-mix(in oklch, var(--brand-gold) 15%, var(--bg-elevated))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 12, color: 'var(--brand-gold)',
            fontFamily: "'Karla', -apple-system, sans-serif",
          }}>S</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Sage is ready when you are.</strong> Your AI assistant monitors projects and surfaces what needs attention.
          </p>
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 20 }}>
          <Link to="/terms" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>Terms</Link>
          <Link to="/privacy" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>Privacy</Link>
          <a href="/dpa" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>DPA</a>
        </div>
      </div>
    </div>
  )
}

